/**
 * One mounted app frame's command channel (RFC 0328 S1).
 *
 * The app owns the command vocabulary. The host owns request identity, timeout,
 * and teardown, because those are properties of the iframe boundary rather
 * than of Review, Calendar, or any other app using it.
 */

export interface AppControlRequest {
  /** Omit to ask the mounted app which commands it declares. */
  action?: string
  input?: Record<string, unknown>
}

export type AppViewController = (request: AppControlRequest) => Promise<unknown>

export interface AppControlWireResult {
  __modBridge?: string
  id?: string | number
  result?: unknown
  error?: string
}

/** What an app declares (RFC 0328 3a). The same shape in either realm. */
export interface AppControlManifest {
  description?: string
  actions: Record<string, { description?: string; inputSchema?: unknown }>
}

export type AppControlHandler = (
  action: string,
  input: Record<string, unknown>,
) => unknown

const DEFAULT_TIMEOUT_MS = 5_000
/**
 * How long a command waits for a view that has not registered its commands
 * yet (a reload, a slow first render). Under `DEFAULT_TIMEOUT_MS`, so the frame
 * shim's wait of the same length still answers before the window gives up.
 */
export const CONTROL_WAIT_MS = 4_000

/**
 * The host-realm half (RFC 0328 3d): an app mounted in the window's own realm
 * registers here, and the controller calls its handler directly.
 *
 * The refusals are the frame shim's, word for word, so `control_app` cannot
 * tell which realm answered. There is no timeout: nothing crosses a boundary
 * that could drop the reply, and a handler that never settles is the app's bug
 * in either realm.
 */
export function createLocalAppControl(): {
  control: AppViewController
  register: (
    manifest: AppControlManifest,
    handler: AppControlHandler,
  ) => () => void
  close: () => void
} {
  let manifest: AppControlManifest | null = null
  let handler: AppControlHandler | null = null
  let closed = false
  /** Commands that arrived before the view registered, told when it does. */
  const waiting = new Set<() => void>()

  /** A view that is still mounting has not registered yet: wait for it,
   *  briefly, rather than answer that the app has no commands. */
  const registered = () =>
    manifest && handler
      ? Promise.resolve(true)
      : new Promise<boolean>((resolve) => {
          const done = () => {
            clearTimeout(timer)
            waiting.delete(done)
            resolve(!!manifest && !!handler)
          }
          const timer = setTimeout(done, CONTROL_WAIT_MS)
          waiting.add(done)
        })

  return {
    async control(request) {
      if (closed) throw new Error('the app view is no longer open')
      if (!(await registered()) || !manifest || !handler)
        throw new Error('this app does not declare view commands')
      if (closed) throw new Error('the app view is no longer open')
      if (!request.action) return manifest
      // Own keys only: `toString` is on every object and is not a command.
      if (!Object.keys(manifest.actions).includes(request.action))
        throw new Error(
          `this app does not declare the "${request.action}" command`,
        )
      return handler(request.action, request.input ?? {})
    },

    register(next, nextHandler) {
      if (!next || typeof next !== 'object' || typeof next.actions !== 'object')
        throw new Error('a control manifest with actions is required')
      if (typeof nextHandler !== 'function')
        throw new Error('a control handler is required')
      manifest = next
      handler = nextHandler
      for (const done of [...waiting]) done()
      return () => {
        if (handler !== nextHandler) return
        manifest = null
        handler = null
      }
    },

    close() {
      closed = true
      manifest = null
      handler = null
      for (const done of [...waiting]) done()
    },
  }
}

/**
 * Build the host half of the request/reply protocol around one postMessage
 * target. Pure apart from timers, so the failure paths do not need an iframe to
 * prove: a late reply, an app error, and an unmounted frame all settle once.
 */
export function createAppViewController(
  post: (message: unknown) => void,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): {
  control: AppViewController
  receive: (message: AppControlWireResult) => boolean
  close: () => void
} {
  let seq = 0
  let closed = false
  const pending = new Map<
    string,
    {
      resolve: (value: unknown) => void
      reject: (error: Error) => void
      timer: ReturnType<typeof setTimeout>
    }
  >()

  const settle = (id: string, value: AppControlWireResult): boolean => {
    const entry = pending.get(id)
    if (!entry) return false
    pending.delete(id)
    clearTimeout(entry.timer)
    if (value.error) entry.reject(new Error(value.error))
    else entry.resolve(value.result)
    return true
  }

  return {
    control(request) {
      if (closed)
        return Promise.reject(new Error('the app view is no longer open'))
      const id = `control-${++seq}`
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          pending.delete(id)
          reject(new Error('the app view did not answer the command'))
        }, timeoutMs)
        pending.set(id, { resolve, reject, timer })
        post({
          __modBridge: 'control',
          id,
          ...(request.action ? { action: request.action } : {}),
          input: request.input ?? {},
        })
      })
    },

    receive(message) {
      if (
        message.__modBridge !== 'control-result' ||
        typeof message.id !== 'string'
      )
        return false
      return settle(message.id, message)
    },

    close() {
      if (closed) return
      closed = true
      for (const [id, entry] of pending) {
        clearTimeout(entry.timer)
        entry.reject(
          new Error('the app view closed before answering the command'),
        )
        pending.delete(id)
      }
    },
  }
}
